/**
 * SOLID Review
 * S — Strong. One use case: play again — the lives restored before the call, the server's round or its absence after.
 * O — Strong. The round trip runs it; a change of rule is the Domain's, of sequence the round trip's.
 * L — Strong. Implements its use-case role and the round trip's three strategies exactly.
 * I — Strong. `execute`, and the three strategies the round trip asks for.
 * D — Strong. The decisions and server ports and the round trip, injected.
 */
import type { IPayloadPortFailure } from '@ml3d/kernel';
import type { IContextRequest } from '../ports/IContextRequest';
import type { IPropsClientTableInput } from '../../domain/ports/schemas/ClientTable';
import type { ICommandNothing, ICommandPlayAgainAnswered, IPayloadPlayAgain } from '../../domain/ports/schemas/TableMoves';
import type { IPayloadServerStartRound } from '../ports/contracts';
import type { TPortOutcome } from '../ports/PortResult';
import type { IUseCase } from '../uml-components/IUseCase';
import type { TServerRoundTripToTheEnd, TAnswered } from '../shared/ServerRoundTrip';
import type { IConfigDepTable } from './IConfigDepTable';

/**
 * Play Again (FE1's playAgain). The plan restores the lives before the call; the answer — or its absence — follows
 * (`runToTheEnd`), and a refusal (no credits) is the answer.
 */
export class PlayAgainUseCase implements IUseCase<ICommandNothing, TPortOutcome>, TServerRoundTripToTheEnd<IPropsClientTableInput, ICommandNothing, IPayloadPlayAgain, TAnswered<NonNullable<ICommandPlayAgainAnswered['answer']>>> {
  constructor(private readonly deps: IConfigDepTable) {}

  execute(ctx: IContextRequest, command: ICommandNothing): Promise<TPortOutcome> {
    return this.deps.trip.runToTheEnd(ctx, command, this);
  }

  plan(ctx: IContextRequest, input: IPropsClientTableInput, command: ICommandNothing) {
    return this.deps.decisions.planPlayAgain(ctx, input, command);
  }

  send(ctx: IContextRequest, plan: IPayloadPlayAgain) {
    const opening: IPayloadServerStartRound = { roomId: plan.roomId, rollerId: plan.rollerId };
    return this.deps.rounds.startRound(ctx, opening);
  }

  answer(ctx: IContextRequest, input: IPropsClientTableInput, plan: IPayloadPlayAgain, round: TAnswered<NonNullable<ICommandPlayAgainAnswered['answer']>> | IPayloadPortFailure) {
    const answered: ICommandPlayAgainAnswered = { request: plan, answer: round.success ? round.value : null };
    return this.deps.decisions.playAgainAnswered(ctx, input, answered);
  }
}
